import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  IconButton,
  Typography,
  Box
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InventoryIcon from '@mui/icons-material/Inventory';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { postData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';

const ItemModal = ({
  open,
  onClose,
  onSave,
  itemData = null,
  mode = 'create', // 'create', 'edit', 'view'
  categories = [],
  existingItems = [] // Add this prop to receive the list of existing items
}) => {
  const { companyId, userId, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  const [formData, setFormData] = useState({
    CategoryId: '',
    Code: '',
    Name: '',
    GSTRate: '',
    HSNCode: '',
    MinRequiredQty: '',
    MRP: '',
    Rate: '',
    RentRate: '',
    PurchaseRate: '',
    OpeningStock: ''
  });

  const [errors, setErrors] = useState({});
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateMessage, setDuplicateMessage] = useState('');

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 600);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (open) {
      console.log('Modal opened with mode:', mode, 'itemData:', itemData);

      if (itemData && mode !== 'create') {
        console.log('Loading existing item data for edit/view, ID:', itemData.Id);
        setFormData({
          Id: itemData.Id || '',
          CategoryId: itemData.CategoryId || '',
          Code: itemData.Code || '',
          Name: itemData.Name || '',
          GSTRate: itemData.GSTRate || 0,
          HSNCode: itemData.HSNCode || '',
          MinRequiredQty: itemData.MinRequiredQty || 0,
          MRP: itemData.MRP || 0,
          Rate: itemData.Rate || 0,
          RentRate: itemData.RentRate || 0,
          PurchaseRate: itemData.PurchaseRate || 0,
          OpeningStock: itemData.OpeningStock || 0
        });
      } else {
        console.log('Creating new item');
        // Reset form for create mode
        setFormData({
          CategoryId: '',
          Code: '',
          Name: '',
          GSTRate: '',
          HSNCode: '',
          MinRequiredQty: '1',
          MRP: '',
          Rate: '',
          RentRate: '',
          PurchaseRate: '',
          OpeningStock: ''
        });
      }
      setErrors({});
    }
  }, [open, itemData, mode]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Validate HSN Code - only allow digits
    if (name === 'HSNCode' && value) {
      const hsnPattern = /^[0-9]*$/;
      if (!hsnPattern.test(value)) {
        return; // Don't update if invalid characters
      }
    }

    // Handle category selection - auto-populate HSN Code and GST Rate
    if (name === 'CategoryId' && value) {
      const selectedCategory = categories.find(cat => cat.Id === parseInt(value));
      if (selectedCategory) {
        const newCode = mode === 'create'
          ? generateItemCode(selectedCategory.Name, formData.Name, existingItems)
          : formData.Code;
        setFormData(prev => ({
          ...prev,
          [name]: value,
          HSNCode: selectedCategory.HSNCode || prev.HSNCode,
          GSTRate: selectedCategory.GSTRate || prev.GSTRate,
          Code: newCode
        }));

        // Clear related errors
        setErrors(prev => ({
          ...prev,
          [name]: '',
          HSNCode: selectedCategory.HSNCode ? '' : prev.HSNCode,
          GSTRate: selectedCategory.GSTRate ? '' : prev.GSTRate
        }));
        return;
      }
    }

    // Auto-regenerate code when Name changes in create mode
    if (name === 'Name' && mode === 'create') {
      const selectedCategory = categories.find(cat => cat.Id === parseInt(formData.CategoryId));
      const newCode = generateItemCode(selectedCategory?.Name, value, existingItems);
      setFormData(prev => ({ ...prev, Name: value, Code: newCode }));
      if (errors.Name) setErrors(prev => ({ ...prev, Name: '' }));
      if (errors.Code) setErrors(prev => ({ ...prev, Code: '' }));
      return;
    }

    // Handle numeric inputs
    if (['GSTRate', 'MinRequiredQty', 'MRP', 'Rate', 'RentRate', 'PurchaseRate', 'OpeningStock'].includes(name)) {
      // Allow only numbers and decimal point
      const numericValue = value.replace(/[^0-9.]/g, '');
      // Ensure only one decimal point
      const parts = numericValue.split('.');
      const formattedValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : numericValue;

      setFormData(prev => ({
        ...prev,
        [name]: formattedValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.CategoryId) {
      newErrors.CategoryId = 'Category is required';
    }

    if (!formData.Code || !formData.Code.trim()) {
      newErrors.Code = 'Item code is required';
    }

    if (!formData.Name || !formData.Name.trim()) {
      newErrors.Name = 'Item name is required';
    }

    if (!formData.GSTRate || formData.GSTRate === '') {
      newErrors.GSTRate = 'GST Rate is required';
    } else {
      const gstRate = parseFloat(formData.GSTRate);
      if (isNaN(gstRate) || gstRate < 0 || gstRate > 100) {
        newErrors.GSTRate = 'GST Rate must be between 0 and 100';
      }
    }

    if (!formData.HSNCode.trim()) {
      newErrors.HSNCode = 'HSN Code is required';
    }

    if (!formData.MinRequiredQty || formData.MinRequiredQty === '') {
      newErrors.MinRequiredQty = 'Minimum required quantity is required';
    } else {
      const minQty = parseFloat(formData.MinRequiredQty);
      if (isNaN(minQty) || minQty < 0) {
        newErrors.MinRequiredQty = 'Minimum quantity must be a positive number';
      }
    }

    if (!formData.MRP || formData.MRP === '') {
      newErrors.MRP = 'MRP is required';
    } else {
      const mrp = parseFloat(formData.MRP);
      if (isNaN(mrp) || mrp <= 0) {
        newErrors.MRP = 'MRP must be a positive number';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const checkDuplicateItem = () => {
    // Check if an item with the same name exists in the list (case-insensitive)
    const duplicate = existingItems.find(item => {
      // Skip the current item in edit mode
      if (mode === 'edit' && itemData && item.Id === itemData.Id) {
        return false;
      }

      // Check if name matches (case-insensitive)
      const nameMatch = item.Name?.trim().toLowerCase() === formData.Name.trim().toLowerCase();

      return nameMatch;
    });

    return duplicate;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    // Check for duplicate item
    const duplicate = checkDuplicateItem();
    if (duplicate) {
      setDuplicateMessage(`An item with the name "${formData.Name}" already exists.`);
      setDuplicateDialogOpen(true);
      return;
    }

    setLoading(true);
    try {
      const dataToSend = {
        CategoryId: parseInt(formData.CategoryId) || 0,
        Code: formData.Code,
        Name: formData.Name,
        GSTRate: parseFloat(formData.GSTRate) || 0,
        HSNCode: formData.HSNCode,
        MinRequiredQty: parseFloat(formData.MinRequiredQty) || 0,
        MRP: parseFloat(formData.MRP) || 0,
        Rate: parseFloat(formData.Rate) || 0,
        RentRate: parseFloat(formData.RentRate) || 0,
        PurchaseRate: parseFloat(formData.PurchaseRate) || 0,
        OpeningStock: parseFloat(formData.OpeningStock) || 0,
        CompanyId: companyId,
      };

      if (mode === 'edit') {
        // Validate that we have the item ID
        if (!itemData || !itemData.Id) {
          console.error('Cannot update: Missing item ID');
          alert('Error: Cannot update item - missing ID');
          return;
        }
        dataToSend.Id = itemData.Id;
        // For edit mode, add modification fields but don't include ID in body (it's in URL)
        dataToSend.ModifiedBy = userId;
        dataToSend.ModifiedDate = new Date();
        // Keep original creation data if available
        if (itemData.CreatedBy) dataToSend.CreatedBy = itemData.CreatedBy;
        if (itemData.CreatedDate) dataToSend.CreatedDate = itemData.CreatedDate;
 
        console.log('Updating item with ID:', itemData.Id);
        console.log('Update data being sent:', dataToSend);
        console.log('API call: PUT /ItemApi/' + itemData.Id);

        const response = await updateData('ItemApi', itemData.Id, dataToSend, token);
        console.log('Update response:', response);
      } else {
        // For create mode, add creation fields
        dataToSend.CreatedBy = userId;
        dataToSend.CreatedDate = new Date();

        console.log('Creating new item');
        console.log('Create data being sent:', dataToSend);
        console.log('API call: POST /ItemApi');

        const response = await postData('ItemApi', dataToSend, token);
        console.log('Create response:', response);
      }

      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving item:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateItemCode = (categoryName, itemName, existingItems) => {
    const catPart = (categoryName || '').replace(/\s+/g, '').substring(0, 2).toUpperCase() || 'XX';
    const namePart = (itemName || '').replace(/\s+/g, '').substring(0, 2).toUpperCase() || 'XX';
    const prefix = `${catPart}-${namePart}`;

    // Find highest existing count for this prefix
    const existing = existingItems
      .map(i => i.Code || '')
      .filter(c => c.startsWith(prefix + '-'))
      .map(c => parseInt(c.split('-')[2]) || 0);

    const nextCount = existing.length > 0 ? Math.max(...existing) + 1 : 1;
    return `${prefix}-${String(nextCount).padStart(3, '0')}`;
  };

  const getTitle = () => {
    switch (mode) {
      case 'create': return 'New Item';
      case 'edit': return 'Edit Item';
      case 'view': return 'Item Details';
      default: return 'Item';
    }
  };

  const isReadOnly = mode === 'view';

  // Common input styles
  const inputStyle = { borderRadius: '6px', fontSize: '13px', height: '32px' };
  const numericInputProps = { style: { textAlign: 'right' } };
  const labelStyle = { fontSize: '11px' };
  const helperStyle = { fontSize: '9px', margin: '1px 0 0 0' };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          style: {
            borderRadius: isMobile ? '0' : '12px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '2px',
            margin: isMobile ? '0' : '8px'
          }
        }}
      >
        <Box style={{
          background: 'white',
          borderRadius: isMobile ? '0' : '10px',
          overflow: 'hidden',
          height: isMobile ? '100vh' : 'auto'
        }}>
          <DialogTitle style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            padding: '8px 12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: 'auto'
          }}>
            <Box style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <InventoryIcon style={{ fontSize: '18px' }} />
              <Typography variant="h6" style={{ fontWeight: '600', fontSize: '14px' }}>
                {getTitle()}
              </Typography>
            </Box>
            <IconButton
              onClick={onClose}
              style={{ color: 'white', padding: '2px' }}
              size="small"
            >
              <CloseIcon style={{ fontSize: '18px' }} />
            </IconButton>
          </DialogTitle>

          <DialogContent style={{
            padding: '8px 12px',
            paddingBottom: '4px'
          }}>
            <Typography variant="subtitle2" style={{
              color: '#666',
              marginBottom: '6px',
              fontSize: '10px',
              lineHeight: '1.2'
            }}>
              {mode === 'create' ? 'Add a new item to your inventory' :
                mode === 'edit' ? 'Update item information' :
                  'View item details'}
            </Typography>

            <Grid container spacing={1}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" style={{
                  fontWeight: '600',
                  marginBottom: '3px',
                  color: '#2d3748',
                  fontSize: '12px'
                }}>
                  Basic Information
                </Typography>
              </Grid>

              {/* Category and Code - Same Row */}
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  name="CategoryId"
                  label="Category *"
                  value={formData.CategoryId}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  error={!!errors.CategoryId}
                  helperText={errors.CategoryId}
                  disabled={isReadOnly}
                  SelectProps={{ native: true }}
                  InputProps={{ style: inputStyle }}
                  InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                >
                  <option value=""></option>
                  {categories.map(category => (
                    <option key={category.Id} value={category.Id}>{category.Name}</option>
                  ))}
                </TextField>
              </Grid>

              {/* Item Name */}
              <Grid item xs={12} sm={6.5}>
                <TextField
                  name="Name"
                  label="Item Name *"
                  value={formData.Name}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  error={!!errors.Name}
                  helperText={errors.Name}
                  disabled={isReadOnly}
                  placeholder="Enter item name"
                  inputProps={{ maxLength: 150 }}
                  InputProps={{ style: inputStyle }}
                  InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>

              <Grid item xs={12} sm={1.5}>
                <TextField
                  name="HSNCode"
                  label="HSN Code *"
                  value={formData.HSNCode}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  error={!!errors.HSNCode}
                  helperText={errors.HSNCode}
                  disabled={isReadOnly}
                  placeholder="Enter HSN code"
                  inputProps={{ maxLength: 10 }}
                  InputProps={{ style: inputStyle }}
                  InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>


              {/* Tax Information */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" style={{
                  fontWeight: '600',
                  marginBottom: '3px',
                  marginTop: '2px',
                  color: '#2d3748',
                  fontSize: '12px'
                }}>
                  Tax Information
                </Typography>
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField
                  name="GSTRate"
                  label="GST Rate (%) *"
                  value={formData.GSTRate}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  error={!!errors.GSTRate}
                  helperText={errors.GSTRate}
                  disabled={isReadOnly}
                  placeholder="0.00"
                  InputProps={{
                    style: inputStyle,
                    endAdornment: <span style={{ fontSize: '12px', color: '#666' }}>%</span>
                  }}
                  inputProps={{ style: { textAlign: 'right' } }}
                  InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField
                  name="PurchaseRate"
                  label="Purchase Rate"
                  value={formData.PurchaseRate}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  disabled={isReadOnly}
                  placeholder="0.00"
                  InputProps={{
                    style: inputStyle,
                    startAdornment: <span style={{ fontSize: '12px', color: '#666', marginRight: '4px' }}>₹</span>
                  }}
                  inputProps={{ style: { textAlign: 'right' } }}
                  InputLabelProps={{ style: labelStyle }}
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField
                  name="MRP"
                  label="MRP *"
                  value={formData.MRP}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  error={!!errors.MRP}
                  helperText={errors.MRP}
                  disabled={isReadOnly}
                  placeholder="0.00"
                  InputProps={{
                    style: inputStyle,
                    startAdornment: <span style={{ fontSize: '12px', color: '#666', marginRight: '4px' }}>₹</span>
                  }}
                  inputProps={{ style: { textAlign: 'right' } }}
                  InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField
                  name="Rate"
                  label="Sale Rate"
                  value={formData.Rate}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  disabled={isReadOnly}
                  placeholder="0.00"
                  InputProps={{
                    style: inputStyle,
                    startAdornment: <span style={{ fontSize: '12px', color: '#666', marginRight: '4px' }}>₹</span>
                  }}
                  inputProps={{ style: { textAlign: 'right' } }}
                  InputLabelProps={{ style: labelStyle }}
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField
                  name="RentRate"
                  label="Rent Rate"
                  value={formData.RentRate}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  disabled={isReadOnly}
                  placeholder="0.00"
                  InputProps={{
                    style: inputStyle,
                    startAdornment: <span style={{ fontSize: '12px', color: '#666', marginRight: '4px' }}>₹</span>
                  }}
                  inputProps={{ style: { textAlign: 'right' } }}
                  InputLabelProps={{ style: labelStyle }}
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <TextField
                  name="OpeningStock"
                  label="Opening Stock"
                  value={formData.OpeningStock}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  disabled={isReadOnly}
                  placeholder="0"
                  InputProps={{ style: inputStyle }}
                  inputProps={{ style: { textAlign: 'right' } }}
                  InputLabelProps={{ style: labelStyle }}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions style={{
            padding: '8px 12px',
            background: '#f8f9fa',
            borderTop: '1px solid #e9ecef',
            gap: '6px',
            minHeight: 'auto'
          }}>
            <Button
              onClick={onClose}
              style={{
                color: '#6c757d',
                textTransform: 'none',
                fontWeight: '500',
                fontSize: '12px',
                padding: '4px 8px'
              }}
            >
              Cancel
            </Button>
            {!isReadOnly && (
              <Button
                onClick={handleSave}
                disabled={loading}
                variant="contained"
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: '600',
                  borderRadius: '4px',
                  padding: '4px 12px',
                  fontSize: '12px'
                }}
              >
                {loading ? 'Saving...' : mode === 'create' ? 'Add Item' : 'Update Item'}
              </Button>
            )}
          </DialogActions>
        </Box>
      </Dialog>

      {/* Duplicate Warning Dialog */}
      <Dialog
        open={duplicateDialogOpen}
        onClose={() => setDuplicateDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '12px',
            padding: '8px'
          }
        }}
      >
        <DialogContent style={{ textAlign: 'center', padding: '24px' }}>
          <WarningAmberIcon
            style={{
              fontSize: '64px',
              color: '#ff9800',
              marginBottom: '16px'
            }}
          />
          <Typography
            variant="h6"
            style={{
              fontWeight: '600',
              marginBottom: '12px',
              color: '#2d3748',
              fontSize: '16px'
            }}
          >
            Duplicate Item Found
          </Typography>
          <Typography
            style={{
              color: '#666',
              fontSize: '14px',
              lineHeight: '1.5'
            }}
          >
            {duplicateMessage}
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: '0 24px 24px 24px', justifyContent: 'center' }}>
          <Button
            onClick={() => setDuplicateDialogOpen(false)}
            variant="contained"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              textTransform: 'none',
              fontWeight: '600',
              borderRadius: '8px',
              padding: '8px 32px',
              fontSize: '14px'
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ItemModal;